import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import {
  BehaviorSubject,
  Observable,
  Subject,
  finalize,
  takeUntil,
} from 'rxjs';
import { ApiBridgeService } from 'src/app/api-bridge.service';
import { StorageService } from 'src/app/shared/services/storage.service';
import { AUTH_TOKEN_KEY, REFRESH_TOKEN_KEY, API_ENDPOINTS } from 'src/app/shared/constants';
import { UserData } from 'src/app/shared/models';

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  // Subjects para estados de loading
  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  public isLoading$ = this.isLoadingSubject.asObservable();

  private isLoadingCuitSubject = new BehaviorSubject<boolean>(false);
  public isLoadingCuit$ = this.isLoadingCuitSubject.asObservable();

  // Subject para usuario autenticado
  private authUserSubject = new BehaviorSubject<string | undefined>(undefined);
  public authUser$ = this.authUserSubject.asObservable();

  // Subject para datos del usuario
  private userDataSubject = new BehaviorSubject<UserData | null>(null);
  public userData$ = this.userDataSubject.asObservable();

  // Subject para el token
  private tokenSubject = new BehaviorSubject<string | null>(null);
  public token$ = this.tokenSubject.asObservable();

  // Subject para cleanup de subscripciones
  private destroy$ = new Subject<void>();

  private readonly jwtHelper = new JwtHelperService();

  constructor(
    private apiBridgeService: ApiBridgeService,
    private router: Router,
    private httpClient: HttpClient,
    private storageService: StorageService
  ) {
    // Inicializar token desde storage
    const storedToken = this.storageService.getItem(AUTH_TOKEN_KEY);
    this.tokenSubject.next(storedToken);
  }

  getToken(): string | null {
    return this.tokenSubject.value;
  }

  isUserTokenExpired(): boolean {
    const token = this.getToken();
    if (!token) return true;
    
    try {
      return this.jwtHelper.isTokenExpired(token);
    } catch {
      return true;
    }
  }

  setToken(token: string): void {
    this.storageService.setItem(AUTH_TOKEN_KEY, token);
    this.tokenSubject.next(token);
    this.authUserSubject.next(token);
  }

  signUp(params: any): Observable<any> {
    this.isLoadingSubject.next(true);
    return this.apiBridgeService
      .post(API_ENDPOINTS.AUTH.SIGNUP, params)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  validateCode(params: any): Observable<any> {
    this.isLoadingSubject.next(true);
    return this.apiBridgeService
      .post(API_ENDPOINTS.AUTH.VALIDATE_CODE, params)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  password(params: any): Observable<any> {
    this.isLoadingSubject.next(true);
    return this.apiBridgeService
      .post(API_ENDPOINTS.AUTH.PASSWORD, params)
      .pipe(
        finalize(() => {
          this.isLoadingSubject.next(false);
          const token = this.storageService.getItem(AUTH_TOKEN_KEY);
          if (token) {
            this.authUserSubject.next(token);
          }
        })
      );
  }

  getUserAuthentication(): Observable<any> {
    this.isLoadingSubject.next(true);
    return this.apiBridgeService
      .get(API_ENDPOINTS.USER.PROFILE)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  forgotPassword(params: any): Observable<any> {
    this.isLoadingSubject.next(true);
    return this.apiBridgeService
      .post(API_ENDPOINTS.AUTH.FORGOT_PASSWORD, params)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  updatePassword(params: any): Observable<any> {
    this.isLoadingSubject.next(true);
    return this.apiBridgeService
      .post(API_ENDPOINTS.AUTH.UPDATE_PASSWORD, params)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  login(params: any): Observable<any> {
    this.isLoadingSubject.next(true);
    return this.apiBridgeService
      .post(API_ENDPOINTS.AUTH.LOGIN, params)
      .pipe(
        finalize(() => {
          this.isLoadingSubject.next(false);
          const token = this.storageService.getItem(AUTH_TOKEN_KEY);
          if (token && token !== 'null') {
            this.setToken(token);
          }
        })
      );
  }

  createUser(params: any): Observable<any> {
    this.isLoadingSubject.next(true);
    return this.apiBridgeService
      .post(API_ENDPOINTS.USER.PROFILE, params)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  getCuitInfo(cuit: string): Observable<any> {
    this.isLoadingCuitSubject.next(true);
    return this.apiBridgeService
      .get(API_ENDPOINTS.USER.CUIT, { cuit })
      .pipe(finalize(() => this.isLoadingCuitSubject.next(false)));
  }

  getCountries(): Observable<any> {
    this.isLoadingSubject.next(true);
    const endpoint = 'assets/json/countries.json';
    return this.httpClient
      .get(endpoint)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  logout(): void {
    this.storageService.clear();
    this.authUserSubject.next(undefined);
    this.userDataSubject.next(null);
    this.tokenSubject.next(null);
    this.router.navigate(['/auth/login']);
  }

  setTokenData(token: string, refreshToken: string): void {
    this.storageService.setItem(AUTH_TOKEN_KEY, token);
    this.storageService.setItem(REFRESH_TOKEN_KEY, refreshToken);
    this.tokenSubject.next(token);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
